<script setup lang="ts" generic="TData">
  import type { Column, Table, VisibilityState } from '@tanstack/vue-table';
  import { computed, type MaybeRef, toValue, useSlots } from 'vue';

  import Dropdown from '../Dropdown/Dropdown.vue';
  import Menu from '../Menu/Menu.vue';
  import MenuItem from '../MenuItem/MenuItem.vue';
  import Switch from '../Switch/Switch.vue';

  export interface DataTableColumnVisibilityProps<TData = unknown> {
    table?: Table<TData> | null;
    /** Visibility slice of `DataTable` `v-model:column-ui` so the list stays reactive. */
    columnVisibility?: MaybeRef<VisibilityState>;
    label?: string;
    align?: 'left' | 'right';
    getColumnLabel?: (column: Column<TData, unknown>) => string;
  }

  const props = withDefaults(defineProps<DataTableColumnVisibilityProps<TData>>(), {
    table: undefined,
    columnVisibility: undefined,
    label: 'Columns',
    align: 'right',
    getColumnLabel: undefined,
  });

  const slots = useSlots();

  function defaultColumnLabel(column: Column<TData, unknown>): string {
    const header = column.columnDef.header;
    return typeof header === 'string' ? header : column.id;
  }

  function labelFor(column: Column<TData, unknown>): string {
    return props.getColumnLabel?.(column) ?? defaultColumnLabel(column);
  }

  /** Stable id shared by the external label and `Switch`’s input (`for` / `id`). */
  function switchIdForColumn(column: Column<TData, unknown>): string {
    return `stash-dt-col-visibility-${String(column.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  const hideableColumns = computed(() => {
    if (props.columnVisibility !== undefined) {
      toValue(props.columnVisibility);
    }

    const table = props.table;
    if (!table) return [];
    // TanStack’s Vue table state is reactive only for slices you read during the computed.
    // `getAllLeafColumns()` alone may not tie this derived list to visibility, order, or
    // pinning updates, so we touch those state slices (via `void`) to re-run when they change.
    void table.getState().columnVisibility;
    void table.getState().columnOrder;
    void table.getState().columnPinning;
    return table.getAllLeafColumns().filter((col) => col.getCanHide());
  });

  function onVisibilitySwitch(column: Column<TData, unknown>, value: boolean | Array<string | number> | undefined) {
    column.toggleVisibility(value === true);
  }
</script>

<template>
  <Dropdown
    v-if="props.table"
    data-test="stash-data-table-column-visibility"
    class="stash-data-table-column-visibility"
    :label="props.label"
    close-manually
    :align="props.align"
  >
    <template v-if="slots.toggle" #toggle="{ isActive, toggle }">
      <slot name="toggle" :is-active="isActive" :toggle="toggle" />
    </template>
    <Menu class="min-w-60">
      <MenuItem v-for="column in hideableColumns" :key="column.id">
        <div class="flex w-full items-center justify-between gap-3 pr-1.5" @click.stop>
          <label class="flex-1 cursor-pointer text-sm text-ice-900" :for="switchIdForColumn(column)">
            {{ labelFor(column) }}
          </label>
          <Switch
            :id="switchIdForColumn(column)"
            :checked="column.getIsVisible()"
            label=""
            @update:checked="(v) => onVisibilitySwitch(column, v)"
          />
        </div>
      </MenuItem>
    </Menu>
  </Dropdown>
</template>
